import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { authService, type InvitationInfo } from '../../services/auth.service';
import PasswordPolicyHints, { validatePassword } from '../../components/auth/PasswordPolicyHints';
import { useSettingsStore } from '../../stores/settingsStore';

/**
 * Accept-invitation page.
 *
 * Lands here from the invitation email (/accept-invite?token=X). Validates the
 * token server-side, shows the invitee's email/tenant/role, collects first/last
 * name and password, then calls POST /auth/accept-invite which creates the KC
 * user and triggers a verify-email action email.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { general } = useSettingsStore();
  const platformName = `${general?.companyName || 'SecureLink'} VSN+`;

  const token = params.get('token') || '';

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ code: string; message: string } | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError({ code: 'MISSING_TOKEN', message: 'This link is missing an invitation token.' });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await authService.validateInvite(token);
        setInfo(data);
      } catch (err: any) {
        const code = err?.response?.data?.error?.code || 'INVALID_TOKEN';
        const message =
          err?.response?.data?.error?.message ||
          'This invitation link is invalid. Ask your administrator for a new one.';
        setLoadError({ code, message });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError('Please enter your first and last name.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }

    if (info?.passwordPolicy) {
      const policyError = validatePassword(info.passwordPolicy, password, info.email);
      if (policyError) {
        setSubmitError(`Password requirement not met: ${policyError}`);
        return;
      }
    } else if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await authService.acceptInvite(token, firstName.trim(), lastName.trim(), password);
      setDone(true);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error?.message ||
          'We could not complete your registration. Please try again or contact your administrator.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // VSNP-687: cross-role activation flow. When the invitee already has an
  // active row in the OTHER user table (sys_user ↔ vpn_users), the API
  // returns crossRoleActivation=true on validate-invite. The recipient
  // doesn't need to set a password — their existing Keycloak account
  // (and password) already work — so we render a single Activate button.
  const handleCrossRoleActivate = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await authService.activateCrossRole(token);
      setDone(true);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error?.message ||
          'We could not complete activation. Please try again or contact your administrator.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Checking your invitation…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Invitation unavailable
          </h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">{loadError.message}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-primary-700 hover:underline"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    // VPN users sign in through the mobile app, not the web portal — never
    // route them to /login. Cross-role activations reuse the existing
    // Keycloak password and send no verification email.
    const isVpn = info?.userType === 3;
    const isCrossRole = !!info?.crossRoleActivation;
    const appName = `${general?.companyName || 'SecureLink'} app`;

    let title: string;
    let body: React.ReactNode;
    if (isCrossRole && isVpn) {
      title = 'VPN access activated';
      body = (
        <>Open the <strong>{appName}</strong> on your device and sign in with your existing {platformName} password.</>
      );
    } else if (isCrossRole) {
      title = 'Access activated';
      body = <>Sign in to {platformName} with your existing password.</>;
    } else if (isVpn) {
      title = 'Account activated';
      body = (
        <>We've sent a verification email to <strong>{info?.email}</strong>. Click the link in that email,
          then open the <strong>{appName}</strong> on your device and sign in.</>
      );
    } else {
      title = 'Account activated';
      body = (
        <>We've sent a verification email to <strong>{info?.email}</strong>. Click the link in that email,
          then sign in to {platformName}.</>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            {title}
          </h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            {body}
          </p>
          {!isVpn && (
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
            >
              Go to sign in
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!info) return null;

  // VSNP-687: cross-role activation page — no password fields, just one
  // Activate button. The existing KC account stays; we only grant the
  // additional client-roles for the new role on the orchestrator side.
  if (info.crossRoleActivation) {
    const newRoleLabel =
      info.userType === 3
        ? 'VPN access'
        : info.userType === 1
          ? 'platform administrator access'
          : info.userType === 5
            ? 'channel administrator access'
            : 'tenant administrator access';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 font-heading">
              Activate {newRoleLabel}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              You already have a {platformName} account for <strong>{info.email}</strong>.
              Click below to add {newRoleLabel} to your existing account — no new password needed.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                value={info.email}
                readOnly
                className="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-600"
              />
            </div>
            {info.tenantName && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tenant</label>
                <input
                  type="text"
                  value={info.tenantName}
                  readOnly
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-600"
                />
              </div>
            )}

            {submitError && (
              <div
                role="alert"
                className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {submitError}
              </div>
            )}

            <button
              type="button"
              onClick={handleCrossRoleActivate}
              disabled={submitting}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Activating…' : `Activate ${newRoleLabel}`}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              Your existing password will continue to work for both roles.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 font-heading">Welcome to {platformName}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {/* A ChannelAdmin joins no tenant — the tenantId carried on their
                invitation is a placeholder the provisioning endpoint demands
                and never turns into an association. Naming it here would be
                actively misleading about their scope. */}
            {info.userType === 5 ? (
              <>Set up your account as a <strong>Channel Administrator</strong>.</>
            ) : (
              <>
                Set up your account to join
                {info.tenantName ? <> <strong>{info.tenantName}</strong></> : null}
                {info.roleName ? <> as <strong>{info.roleName}</strong></> : null}.
              </>
            )}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                value={info.email}
                readOnly
                className="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={submitting}
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={submitting}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Choose a password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordPolicyHints policy={info.passwordPolicy} password={password} email={info.email} />
            </div>

            <div className="mb-5">
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm password
              </label>
              <input
                id="confirm"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitting}
                required
              />
            </div>

            {submitError && (
              <div
                role="alert"
                className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Activating account…' : 'Activate account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
